import { Alert, MultiSelectOptionType, Stack, useNotifications } from '@sixfold/common-ui';
import { TpRtvWebhookTopicRuntype, type TpRtvWebhookTopic } from '@sixfold/public-api-interface';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { WebhookSubscriptionHeader } from './webhook_subscription_header';
import { WebhookSubscriptionTable } from './webhook_subscription_table';
import {
  CompanyWebhookSubscriptionInfo,
  CreateWebhookSubscriptionInput,
  DeleteWebhookSubscriptionInput,
  UpdateCompanyWebhookSubscriptionStatus,
  UpdateWebhookSubscriptionInput,
  WebhookPayloadContentType,
} from '../../../lib/graphql';
import { Routes } from '../../../routes';
import {
  buildDiscriminatorForTopics,
  extractPlatformIdFromDiscriminator,
  parseDiscriminator,
} from '../webhook_discriminator_field';

import styles from '../../containers/styles.module.css';

export interface EditState {
  url: string;
  status: string;
  payloadContentType: string;
  expirationDate: string;
  contactEmails: MultiSelectOptionType[];
  topics: string[];
  headers: { [key: string]: string | number | boolean | object };
  platformId: string;
}

export interface ValidationResult {
  isValid: boolean;
  isEmailInvalid: boolean;
  isUrlEmpty: boolean;
  hasNoTopics: boolean;
  hasDiscriminatorError: boolean;
}

interface CreateModeProps {
  mode: 'create';
  company_id: string;
  onCreate: (input: CreateWebhookSubscriptionInput) => Promise<string>;
}

interface EditModeProps {
  mode: 'edit';
  company_id: string;
  subscription: CompanyWebhookSubscriptionInfo;
  onSave: (input: UpdateWebhookSubscriptionInput) => Promise<void>;
  onDelete: (input: DeleteWebhookSubscriptionInput) => Promise<void>;
}

export type WebhookSubscriptionFormProps = CreateModeProps | EditModeProps;

const TP_RTV_TOPICS = TpRtvWebhookTopicRuntype.alternatives.map((literal) => literal.value);
const POSITION_UPDATE_TOPIC: TpRtvWebhookTopic = 'position-update';

function validateWebhookForm(editState: EditState): ValidationResult {
  const isEmailInvalid = editState.contactEmails.some((email) => email.invalid);
  const isUrlEmpty = editState.url.trim() === '';
  const hasNoTopics = editState.topics.length === 0;

  const hasTpRtvTopicsSelected = editState.topics.some((topic) => TP_RTV_TOPICS.includes(topic as TpRtvWebhookTopic));
  const hasDiscriminatorError = hasTpRtvTopicsSelected && editState.platformId.trim() === '';

  return {
    isValid: !isEmailInvalid && !isUrlEmpty && !hasNoTopics && !hasDiscriminatorError,
    isEmailInvalid,
    isUrlEmpty,
    hasNoTopics,
    hasDiscriminatorError,
  };
}

function getDefaultExpirationDate(): string {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 10);
  return date.toISOString();
}

function getCreateModeInitialState(): EditState {
  return {
    url: '',
    status: UpdateCompanyWebhookSubscriptionStatus.WORKING,
    payloadContentType: WebhookPayloadContentType.JSON,
    expirationDate: getDefaultExpirationDate(),
    contactEmails: [],
    topics: [],
    headers: {},
    platformId: '',
  };
}

function getEditModeInitialState(subscription: CompanyWebhookSubscriptionInfo): EditState {
  const discriminator = parseDiscriminator(subscription.discriminator ?? {});
  return {
    url: subscription.url,
    status: subscription.status,
    payloadContentType: subscription.payloadContentType,
    expirationDate: subscription.expirationDate,
    contactEmails: subscription.contactEmails.map((email) => ({ value: email, label: email })),
    topics: subscription.topics,
    headers: subscription.headers,
    platformId: extractPlatformIdFromDiscriminator(discriminator),
  };
}

export function WebhookSubscriptionForm(props: WebhookSubscriptionFormProps) {
  const { slug_company_name } = useParams<{ slug_company_name?: string }>();
  const isCreateMode = props.mode === 'create';
  const subscription = props.mode === 'edit' ? props.subscription : null;

  const notify = useNotifications();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(isCreateMode);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const [editState, setEditState] = useState<EditState>(() =>
    isCreateMode ? getCreateModeInitialState() : getEditModeInitialState(props.subscription),
  );

  const validation = validateWebhookForm(editState);

  const handleCancel = () => {
    if (isCreateMode) {
      void navigate(-1);
      return;
    }
    setEditState(getEditModeInitialState(props.subscription));
    setIsEditing(false);
    setHasAttemptedSave(false);
  };

  const handleSave = async () => {
    setHasAttemptedSave(true);
    if (!validation.isValid) {
      return;
    }

    setIsSaving(true);
    try {
      const discriminator = buildDiscriminatorForTopics(editState.platformId, editState.topics);

      if (props.mode === 'create') {
        const subscriptionId = await props.onCreate({
          companyId: props.company_id,
          url: editState.url,
          payloadContentType: editState.payloadContentType as WebhookPayloadContentType,
          expirationDate: editState.expirationDate,
          contactEmails: editState.contactEmails.map((e) => e.value),
          topics: editState.topics,
          headers: editState.headers,
          discriminator,
        });
        notify.success({ title: 'Webhook subscription created' });
        void navigate(
          Routes.CompanyWebhookSubscription.generatePath({
            company_id: props.company_id,
            webhook_subscription_id: subscriptionId,
            slug_company_name,
          }),
          { replace: true },
        );
      } else if (props.mode === 'edit') {
        await props.onSave({
          companyId: props.company_id,
          subscriptionId: props.subscription.subscriptionId,
          url: editState.url,
          status: editState.status as UpdateCompanyWebhookSubscriptionStatus,
          payloadContentType: editState.payloadContentType as WebhookPayloadContentType,
          expirationDate: editState.expirationDate,
          contactEmails: editState.contactEmails.map((e) => e.value),
          topics: editState.topics,
          headers: editState.headers,
          discriminator,
        });
        notify.success({ title: 'Webhook subscription updated' });
        setIsEditing(false);
      }
    } catch (error) {
      console.error(error);
      notify.error({ title: `Failed to ${isCreateMode ? 'create' : 'update'} webhook subscription` });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (props.mode !== 'edit') {
      return;
    }

    setIsDeleting(true);

    try {
      await props.onDelete({
        companyId: props.company_id,
        subscriptionId: props.subscription.subscriptionId,
      });
      notify.success({ title: 'Webhook subscription deleted' });
      void navigate(
        Routes.CompanyTelematicsIntegrations.generatePath({ company_id: props.company_id, slug_company_name }),
      );
    } catch (error) {
      console.error(error);
      notify.error({ title: 'Failed to delete webhook subscription' });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const hasPositionTopicSelected = editState.topics.includes(POSITION_UPDATE_TOPIC);

  return (
    <Stack direction="column" className={styles.webhookSubscriptionContainer}>
      <WebhookSubscriptionHeader
        isCreateMode={isCreateMode}
        isEditing={isEditing}
        isSaving={isSaving}
        isDeleting={isDeleting}
        showDeleteConfirm={showDeleteConfirm}
        onEdit={() => setIsEditing(true)}
        onCancel={handleCancel}
        onSave={handleSave}
        onDelete={handleDelete}
        onToggleDeleteConfirm={() => setShowDeleteConfirm(!showDeleteConfirm)}
      />
      {isEditing && hasPositionTopicSelected && (
        <Alert kind="warning">
          <strong>
            You selected the &quot;{POSITION_UPDATE_TOPIC}&quot; topic - Legal checks required for EU customers
          </strong>
          <div>
            <p>
              For EU customers, webhook with position data can only be offered after dealing with Legal checks. We
              can&apos;t share vehicle GPS positions with EU shippers until all carriers have signed the Data Processing
              Agreement (DPA).
            </p>
            <p>
              <strong>What the Project Team can do to get GPS data via a webhook:</strong>
            </p>
            <ol>
              <li>
                <strong>Check Shipper Carriers&apos; PUA:</strong> Check if all carriers have the latest PUA that
                includes the DPA. Legal is working to get automatic DPA confirmation from carriers, don&apos;t know how
                far they are with this initiative.
              </li>
              <li>
                <strong>Ask Legal About a Special Addendum:</strong> Ask the legal team if shipper can sign a special
                addendum, to get the API up and running before legal PUA update with carriers is sorted.
              </li>
            </ol>
            <p>These steps should help us stay on the right side of the law while working to meet customer needs.</p>
            <hr />
            <p>
              <em>Addendum was asked for Shell by project manager Melanie Herrou in ticket: LEGAL-21563</em>
            </p>
            <hr />
            <p>
              More details:{' '}
              <a
                href="https://www.notion.so/trimble-tl/Legal-stand-on-gps-sharing-2c3c1b3dd25680c5b953c04ef6a1e332"
                target="_blank"
                rel="noopener noreferrer">
                Legal stand on GPS sharing (Notion)
              </a>
            </p>
          </div>
        </Alert>
      )}
      <WebhookSubscriptionTable
        isCreateMode={isCreateMode}
        isEditing={isEditing}
        hasAttemptedSave={hasAttemptedSave}
        editState={editState}
        validation={validation}
        subscription={subscription}
        onEditStateChange={setEditState}
      />
    </Stack>
  );
}
